import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { EmbedFrame } from "@/components/embed/EmbedFrame";
import { runHealthCheck } from "@/lib/embeds.functions";

export const Route = createFileRoute("/embed/health-check")({
  head: () => ({
    meta: [
      { title: "GBP Health Check — Map Observer embed" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: HealthCheckEmbed,
});

type State =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "result"; data: Awaited<ReturnType<typeof runHealthCheck>> }
  | { kind: "error"; message: string };

function HealthCheckEmbed() {
  const run = useServerFn(runHealthCheck);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !city.trim()) return;
    setState({ kind: "loading" });
    try {
      const data = await run({ data: { name: name.trim(), city: city.trim() } });
      setState({ kind: "result", data });
    } catch (err) {
      setState({ kind: "error", message: err instanceof Error ? err.message : "Check failed" });
    }
  }

  return (
    <EmbedFrame name="health-check">
      <div
        style={{
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          color: "#1a1612",
          border: "1px solid #d9cfbf",
          borderRadius: 6,
          background: "#fbf7ef",
          padding: 16,
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#6b6357" }}>
          Free Google Business Profile health check
        </div>
        <h2 style={{ fontSize: 18, margin: "4px 0 12px", fontWeight: 700 }}>How visible is your business on Google?</h2>
        <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Business name"
            required
            style={inputStyle}
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City, ST"
            required
            style={inputStyle}
          />
          <button type="submit" disabled={state.kind === "loading"} style={buttonStyle}>
            {state.kind === "loading" ? "Checking…" : "Check"}
          </button>
        </form>

        {state.kind === "error" && (
          <p style={{ marginTop: 12, fontSize: 13, color: "#7c1d1d" }}>{state.message}</p>
        )}

        {state.kind === "result" && state.data.ok && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: "#6b6357", marginBottom: 6 }}>
              Query: <span style={{ color: "#1a1612", fontWeight: 600 }}>{state.data.query}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {state.data.signals.map((s) => (
                <li
                  key={s.label}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    background: "#fff",
                    border: "1px solid #ece3d2",
                    borderRadius: 4,
                    padding: "8px 10px",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      marginTop: 6,
                      flexShrink: 0,
                      background:
                        s.status === "pass"
                          ? "#2f6b3a"
                          : s.status === "warn"
                          ? "#c98b2a"
                          : s.status === "fail"
                          ? "#7c1d1d"
                          : "#6b6357",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: "#3a342a" }}>{s.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {state.kind === "result" && !state.data.ok && (
          <p style={{ marginTop: 12, fontSize: 13, color: "#7c1d1d" }}>
            {state.data.error ?? "Check failed."}
          </p>
        )}
      </div>
    </EmbedFrame>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid #d9cfbf",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 13,
  background: "#fff",
  color: "#1a1612",
  minWidth: 0,
};

const buttonStyle: React.CSSProperties = {
  background: "#1a1612",
  color: "#fbf7ef",
  border: 0,
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 1,
  cursor: "pointer",
};
